import * as React from 'react';
import { useMemo } from 'react';
import cx from 'classnames';
import { observer } from 'mobx-react';
import styles from './index.less';
import wrapStore from '@src/utils/wrap-store';
import NineSquarePrize from '@src/components/nine-square-prize';
import DrawStore from '@src/pages/main/components/draw-section/store';
import Link from '@src/components/link';

// 任务项组件
const MissionItem = observer(({ mission, onClick, className, activeClassName, children }) => {
  const Component = mission.completed ? 'div' : Link;
  const props = mission.completed
    ? { className: cx(activeClassName) }
    : { className: cx(className), onClick, ...(mission.href && { href: mission.href, target: '_blank' }) };

  return <Component {...props}>{children}</Component>;
});

// 游戏登录状态组件
const GameLoginStatus = observer(({ store }) => {
  return (
    <React.Fragment>
      <Link onClick={store.onClickGameLogin} className={store.todayLoginGameState || store.loginGameTimes >= 5 ? styles.thirdPartAct : styles.thirdPart}>
        {store.gameLoginProgressText}
      </Link>
      <div className={styles.dayList}>
        {[1, 2, 3, 4, 5].map(day => (
          <div key={day} className={cx(store.loginGameTimes >= day ? styles.dayItemActive : styles.dayItem)}>
            <span className={styles.dayItemNum}>{day}</span>

            <div className={styles.dayTimes}>{store.loginGameTimes >= day ? '已获得' : '未获得'}</div>
          </div>
        ))}
      </div>
      {!store.loginGameState && <div className={styles.dayListNot} />}
    </React.Fragment>
  );
});

// 任务列表组件
const MissionList = observer(({ store, handleMissionClick }) => {
  const firstPartMissions = store.missionConfigs.slice(0, 2);
  const secondPartMissions = store.missionConfigs.slice(2);

  return (
    <React.Fragment>
      <div className={styles.firstPartBox}>
        {firstPartMissions.map(mission => (
          <MissionItem
            key={mission.type}
            mission={mission}
            onClick={() => handleMissionClick(mission.type)}
            className={styles.firstPart}
            activeClassName={styles.firstPartAct}
          >
            {mission.title}
            <span>{mission.completed && mission.type !== 'dailyShare' ? `${mission.reward}已获得` : mission.reward}</span>
          </MissionItem>
        ))}
      </div>

      <div className={styles.secondPartBox}>
        {secondPartMissions.map(mission => (
          <MissionItem
            key={mission.type}
            mission={mission}
            onClick={() => handleMissionClick(mission.type)}
            className={styles.secondPart}
            activeClassName={styles.secondPartAct}
          >
            {mission.title}

            <span>{mission.completed && mission.type !== 'dailyShare' ? `${mission.reward}已获得` : mission.reward}</span>
          </MissionItem>
        ))}
      </div>
    </React.Fragment>
  );
});

// 登录信息组件
const LoginInfo = observer(({ store }) => {
  return (
    <React.Fragment>
      {store.mainStore.loginState ? (
        <div className={styles.loginInfo}>
          <div className={styles.account}>{store?.mainStore?.loginUserAccount}</div>
          <Link className={styles.logoutBtn} onClick={store.onClickLogout}>
            退出
          </Link>
        </div>
      ) : (
        <Link className={styles.loginBtn} onClick={store.onClickLogin} />
      )}
    </React.Fragment>
  );
});

const Draw = ({ store }) => {
  store.use();
  /**
   * 渲染抽奖节点
   * @param item
   * @param index
   * @param isAct
   * @returns {JSX.Element}
   */
  const renderDrawItem = (item, index, isAct = false) => {
    const positionClass = `positionItem${item.id}` || '';
    return (
      <div key={index} className={cx(styles.drawItem, isAct && styles.drawItemActive)}>
        <img src={item.btn_url} className={positionClass} alt="" />
      </div>
    );
  };

  /**
   *  抽奖
   */
  const renderNineSquarePrize = useMemo(
    () => {
      if (store.drawAwardList) {
        const btnDraw = <Link className={cx(styles.drawBtn, store.mainStore.loginState && store.prizeChance <= 0 && styles.drawBtnDisable)} />;
        let draw_rewards_list_el = [];
        let draw_rewards_list_el_active = [];
        store.drawAwardList.forEach((item, index) => {
          item.id = index + 1;
          item.index = index + 1;
          draw_rewards_list_el.push(renderDrawItem(item, index));
          draw_rewards_list_el_active.push(renderDrawItem(item, index, true));
        });

        return (
          <React.Fragment>
            <div className={styles.prizeListBg} />
            <NineSquarePrize
              className={styles.prizeList}
              prizeList={store.drawAwardList}
              itemNormalElement={draw_rewards_list_el}
              itemActiveElement={draw_rewards_list_el_active}
              activeBtnElement={btnDraw}
              prizeChance={store.prizeChance}
              getPrizeResult={store.getPrizeResult}
              prizeComplete={result => {
                store.prizeComplete(result);
              }}
              account={store.mainStore.loginState}
              showAppointmentModal={() => {
                if (!store.mainStore.loginState) {
                  store.mainStore.onLogin();
                }
              }}
            />
          </React.Fragment>
        );
      }
    },
    [store.drawAwardList, store.prizeChance, store.mainStore.loginState]
  );

  return (
    <div className={cx(styles.drawRoot, store.isActive && styles.currentPage)}>
      <div className={styles.bg} />
      <div className={styles.title} />
      <div className={styles.content}>
        <div className={styles.leftContent}>
          <div className={styles.modalBtn}>
            <Link
              className={cx(store.isReceiveAwardBtnDisabled ? styles.modalBtnItem1Disable : styles.modalBtnItem, styles.modalBtnItem1)}
              onClick={() => store.onClickOpenReceiveAwardModal(false)}
            />
            <Link className={cx(styles.modalBtnItem, styles.modalBtnItem2)} onClick={store.onClickOpenRule} />
            <Link className={cx(styles.modalBtnItem, styles.modalBtnItem3)} onClick={store.onClickOpenRecordListModal} />
          </div>

          {renderNineSquarePrize}

          <div className={styles.prizeChance}>
            <Link className={styles.refreshBtn} onClick={store.clickRefresh} />
            <span>剩余次数{store.prizeChance}次</span>
          </div>
        </div>
        <div className={styles.rightContent}>
          <LoginInfo store={store} />
          <div className={styles.missionArea}>
            <MissionList store={store} handleMissionClick={type => store.onAddChanceByExecuteMission(type)} />
            <GameLoginStatus store={store} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default wrapStore(Draw, DrawStore);
